import React, { useState, useEffect, useMemo } from 'react';
import { Card, Tabs, Row, Col, DatePicker, Select, Button, Statistic, Progress, List, Tag, Badge } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LineChart, Line } from 'recharts';
import dayjs from 'dayjs';
import api from '../../api/http';
import { resolveApiUrl } from '../../api/http';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DataAnalysisView = () => {
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [department, setDepartment] = useState('');
  const [departments, setDepartments] = useState([]);

  // 颜色配置
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  // 初始化数据
  useEffect(() => {
    fetchDepartments();
    fetchStatistics();
  }, []);

  // 获取部门列表
  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      if (response && response.data) {
        setDepartments(response.data);
      }
    } catch (error) {
      console.error('获取部门列表失败:', error);
    }
  };

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      setLoading(true);
      
      const params = {
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD')
      };
      
      if (department) {
        params.department = department;
      }

      const response = await api.get('/leave-overtime/ledger/stats', { params });
      if (response && response.data) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理筛选条件变化
  const handleFilterChange = () => {
    fetchStatistics();
  };

  // 处理日期范围变化
  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  // 处理部门选择变化
  const handleDepartmentChange = (value) => {
    setDepartment(value);
  };

  // 格式化饼图数据
  const formatPieData = (data) => {
    if (!data || data.length === 0) return [];
    
    return data.map((item, index) => ({
      name: item.type || item.record_type,
      value: item.count || item.total_hours,
      itemStyle: {
        color: COLORS[index % COLORS.length]
      }
    }));
  };

  // 格式化柱状图数据（按部门）
  const formatDepartmentBarData = (data) => {
    if (!data || data.length === 0) return [];
    
    return data.map(item => ({
      name: item.department,
      请假时长: item.leave_hours,
      加班时长: item.overtime_hours,
      总记录数: item.total_records
    }));
  };

  // 格式化类型统计数据
  const formatTypeStats = (data) => {
    if (!data || data.length === 0) return [];
    
    const leaveStats = data.filter(item => item.record_type === 'LEAVE');
    const overtimeStats = data.filter(item => item.record_type === 'OVERTIME');
    
    return [
      { name: '请假类型', data: leaveStats },
      { name: '加班类型', data: overtimeStats }
    ];
  };

  // 生成模拟趋势数据（按月份）
  const generateTrendData = () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const month = dayjs().subtract(i, 'month');
      months.push(month.format('YYYY-MM'));
    }
    
    return months.map(month => {
      const leaveHours = Math.floor(Math.random() * 100) + 50;
      const overtimeHours = Math.floor(Math.random() * 150) + 80;
      return {
        month,
        请假时长: leaveHours,
        加班时长: overtimeHours
      };
    });
  };

  const trendData = useMemo(() => generateTrendData(), []);
  const pieData = useMemo(() => formatPieData(statistics?.type_stats || []), [statistics]);
  const departmentBarData = useMemo(() => formatDepartmentBarData(statistics?.department_stats || []), [statistics]);
  const typeStats = useMemo(() => formatTypeStats(statistics?.type_stats || []), [statistics]);

  // 渲染概览统计
  const renderOverview = () => {
    if (!statistics?.summary) return null;
    
    const { summary } = statistics;
    const totalHours = (summary.total_leave_hours || 0) + (summary.total_overtime_hours || 0);
    const leavePercentage = totalHours > 0 ? ((summary.total_leave_hours || 0) / totalHours) * 100 : 0;
    const overtimePercentage = totalHours > 0 ? ((summary.total_overtime_hours || 0) / totalHours) * 100 : 0;

    return (
      <Row gutter={16}>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic 
              title="总记录数" 
              value={summary.total_records} 
              loading={loading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic 
              title="请假总时长" 
              value={summary.total_leave_hours} 
              suffix="小时"
              loading={loading}
            />
            <Progress percent={leavePercentage} strokeColor="#1890ff" size="small" />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic 
              title="加班总时长" 
              value={summary.total_overtime_hours} 
              suffix="小时"
              loading={loading}
            />
            <Progress percent={overtimePercentage} strokeColor="#52c41a" size="small" />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic 
              title="涉及员工数" 
              value={summary.unique_employees} 
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染状态分布饼图
  const renderStatusDistribution = () => {
    if (!statistics?.summary) return null;
    
    const { summary } = statistics;
    const statusData = [
      { name: '已批准', value: summary.approved_count || 0 },
      { name: '待审批', value: summary.pending_count || 0 },
      { name: '已拒绝', value: summary.rejected_count || 0 }
    ];

    return (
      <Card title="审批状态分布" loading={loading}>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={statusData}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {statusData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // 渲染类型分布饼图
  const renderTypeDistribution = () => {
    return (
      <Card title="请假/加班类型分布" loading={loading}>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // 渲染部门对比柱状图
  const renderDepartmentComparison = () => {
    return (
      <Card title="部门对比分析" loading={loading}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={departmentBarData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="请假时长" fill="#1890ff" />
            <Bar dataKey="加班时长" fill="#52c41a" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // 渲染趋势分析图
  const renderTrendAnalysis = () => {
    return (
      <Card title="请假加班趋势分析" loading={loading}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={trendData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="请假时长" stroke="#1890ff" activeDot={{ r: 8 }} />
            <Line type="monotone" dataKey="加班时长" stroke="#52c41a" />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // 渲染类型统计列表
  const renderTypeStatsList = () => {
    return (
      <Card title="类型统计详情" loading={loading}>
        {typeStats.map((category, index) => (
          <div key={index} style={{ marginBottom: 20 }}>
            <h3 style={{ marginBottom: 10 }}>{category.name}</h3>
            <List
              dataSource={category.data}
              renderItem={item => (
                <List.Item
                  key={item.type}
                  actions={[
                    <Badge key="hours" count={item.total_hours} style={{ backgroundColor: '#52c41a' }} />
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <span>
                        <Tag color={COLORS[index % COLORS.length]}>{item.type}</Tag>
                      </span>
                    }
                    description={`${item.count} 条记录，总计 ${item.total_hours} 小时`}
                  />
                </List.Item>
              )}
            />
          </div>
        ))}
      </Card>
    );
  };

  // 导出统计数据
  const handleExportStatistics = () => {
    // 构建导出参数
    const params = new URLSearchParams();
    if (department) params.append('department', department);
    params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
    params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
    
    // 打开下载链接
    window.open(resolveApiUrl(`/api/leave-overtime/export/statistics?${params.toString()}`), '_self');
  };

  return (
    <Card title="数据分析" className="data-analysis-view">
      {/* 筛选条件 */}
      <Row gutter={16} style={{ marginBottom: 20, alignItems: 'center' }}>
        <Col span={8}>
          <RangePicker 
            value={dateRange}
            onChange={handleDateRangeChange}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={6}>
          <Select 
            placeholder="选择部门（可选）"
            style={{ width: '100%' }}
            value={department}
            onChange={handleDepartmentChange}
            allowClear
          >
            {departments.map(dept => (
              <Option key={dept.id} value={dept.id}>{dept.name}</Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Button type="primary" onClick={handleFilterChange} loading={loading}>
            分析数据
          </Button>
        </Col>
        <Col span={4} offset={2}>
          <Button type="primary" onClick={handleExportStatistics} icon={<DownloadOutlined />}>
            导出统计数据
          </Button>
        </Col>
      </Row>

      {/* 统计概览 */}
      {renderOverview()}

      {/* 图表分析 */}
      <Tabs defaultActiveKey="1" style={{ marginTop: 20 }}>
        <TabPane tab="状态与类型分析" key="1">
          <Row gutter={16}>
            <Col span={12}>
              {renderStatusDistribution()}
            </Col>
            <Col span={12}>
              {renderTypeDistribution()}
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              {renderTypeStatsList()}
            </Col>
          </Row>
        </TabPane>
        <TabPane tab="部门对比分析" key="2">
          <Row gutter={16}>
            <Col span={24}>
              {renderDepartmentComparison()}
            </Col>
          </Row>
        </TabPane>
        <TabPane tab="趋势分析" key="3">
          <Row gutter={16}>
            <Col span={24}>
              {renderTrendAnalysis()}
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default DataAnalysisView;